import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Mail, Sparkles } from 'lucide-react';
import theme from '../theme';
import GuidedTour from '../components/GuidedTour';

const ROLE_DATA = {
  creator: {
    label: 'Creator',
    quickLinks: [
      ['Create your first portfolio', '/creator/portfolios/new'],
      ['Browse contract templates',   '/creator/contracts/templates'],
      ['Publish a utility',           '/creator/publish-utility'],
      ['View payouts',                '/creator/payouts'],
      ['Open disputes',               '/creator/disputes'],
      ['Check contract quota',        '/creator/billing'],
    ],
    fiveMin: [
      'Open Dashboard and review your active portfolios + contracts remaining tile.',
      'Click "Create Portfolio" and fill name, kickback %, units.',
      'Pick a contract template and create your first draft.',
      'Connect MetaMask + deploy on Polygon Amoy.',
      'Attach a utility from the marketplace to your portfolio.',
    ],
    faqs: [
      ['How do I create my first portfolio?', 'Click the blue "Create Portfolio" button on the dashboard. Fill name, description, kickback % and unit price; you can attach utilities later.'],
      ['What is kickback %?',                 'The percentage of every sale that returns to buyers as a reward. It is set per portfolio and shown on every NFT card.'],
      ['How do I deploy a contract?',         'From a contract draft, click Deploy. Connect MetaMask, switch to Polygon Amoy, click Compile then Deploy. Backend verifies the receipt on-chain.'],
      ['Why does my deploy say address mismatch?', 'The on-chain receipt contractAddress did not match the address you submitted. Open the tx on Polygonscan and re-deploy with the correct address.'],
      ['Where do I see my revenue?',          'On /creator/payouts. KPI cards plus a per-portfolio breakdown — every row is clickable to that portfolio.'],
      ['What if my payouts engine is disabled?', 'A yellow banner is shown but real performance metrics still aggregate from the sales collection.'],
      ['How do contract drafts count against quota?', 'Each draft you create counts as one used contract. Used / Remaining / Total are visible on /creator/billing.'],
      ['How do I respond to a dispute?',      'Open the dispute detail to see buyer, NFT, transfer, attachments, and admin notes. Admins resolve disputes; creators have read-only access.'],
    ],
  },
  buyer: {
    label: 'Holder',
    quickLinks: [
      ['Marketplace',          '/buyer/marketplace'],
      ['My NFTs',              '/buyer/dashboard'],
      ['Transfers',            '/buyer/transfers'],
      ['Utilities',            '/buyer/utilities'],
      ['Payouts',              '/buyer/payouts'],
      ['Settings',             '/buyer/settings'],
    ],
    fiveMin: [
      'Browse Marketplace and open a portfolio detail.',
      'Buy an NFT and watch the receipt land in your dashboard.',
      'Open the NFT detail and inspect attached utilities.',
      'Add a wallet under Settings.',
      'File a dispute on a problem purchase if needed.',
    ],
    faqs: [
      ['How do I buy an NFT?', 'On Marketplace click any portfolio card → buy modal opens. Choose quantity and confirm.'],
      ['Where are my purchased NFTs?', 'Owned NFTs tab on /buyer/dashboard. Click any card for full metadata, utilities and transfer history.'],
      ['What is a transfer?', 'A transfer of NFT ownership (initial mint, secondary trade, or transfer by you). Track them on /buyer/transfers.'],
      ['How do I redeem a utility?', 'Open the NFT or utility detail; redemption instructions are listed under "How to redeem".'],
      ['What if my redemption fails?', 'File a dispute from the NFT detail. The creator and admin will see your ticket.'],
      ['How are payouts calculated?', 'Kickback % is paid back to your account on each sale your NFT participated in. History is on /buyer/payouts.'],
      ['Can I sell an NFT?', 'Secondary trading depends on the contract type and is exposed on each NFT detail page when supported.'],
      ['How do I update payout method?', 'Settings → Payout Preferences. Configure ACH / PayPal / crypto split and save.'],
    ],
  },
  broker: {
    label: 'Broker',
    quickLinks: [
      ['Opportunities (Dashboard)', '/broker/dashboard'],
      ['Active portfolios',         '/broker/portfolios'],
      ['Directory profile',         '/broker/directory'],
      ['Disputes',                  '/broker/disputes'],
      ['Payouts',                   '/broker/payouts'],
      ['Settings',                  '/broker/settings'],
    ],
    fiveMin: [
      'Open Opportunities and accept your first portfolio.',
      'Visit Active to see what you broker.',
      'Edit your Directory profile so creators can find you.',
      'Configure your specialties under Settings.',
      'Skim Payouts to understand commission structure.',
    ],
    faqs: [
      ['How do I get an opportunity?', 'Creators invite you. Open Opportunities tab and Accept or Decline.'],
      ['What is the Directory?', 'Your public broker profile that creators search to invite you.'],
      ['How are commissions paid?', 'A fixed cut of each sale of portfolios you broker. See breakdown on /broker/payouts.'],
      ['What does Active show?', 'Portfolios you accepted that are currently live and producing sales.'],
      ['Can I refuse an active portfolio?', 'You can step down via Settings; the portfolio reverts to Self-Managed or to another broker.'],
      ['How are disputes handled?', 'Read-only view; admins resolve. Use the detail page to inform creators if needed.'],
      ['How do I update specialties?', 'Settings → Specialties. Saved values surface on the public Directory profile.'],
      ['How is my membership tier set?', 'Driven by your accepted opportunities + total revenue brokered.'],
    ],
  },
  admin: {
    label: 'Admin',
    quickLinks: [
      ['Overview', '/admin/dashboard?tab=overview'],
      ['Invites',  '/admin/dashboard?tab=invites'],
      ['Disputes', '/admin/dashboard?tab=disputes'],
      ['Users',    '/admin/dashboard?tab=users'],
      ['Portfolios','/admin/dashboard?tab=portfolios'],
      ['Plans',    '/admin/dashboard?tab=plans'],
    ],
    fiveMin: [
      'Open Overview and review platform KPIs.',
      'Issue a Holder invite from the Invites tab.',
      'Triage one dispute (Disputes tab) — change status + add a note.',
      'Approve a pending creator (Approvals tab).',
      'Toggle a Plan and verify the price applied.',
    ],
    faqs: [
      ['Who can self-register?', 'Only creator and broker. Holder/Admin self-register is blocked (HTTP 403). Use the Invites tab.'],
      ['How do I issue an invite?', 'Invites tab → enter email + role → Generate. Copy the link from the row that appears.'],
      ['How do I resolve a dispute?', 'Open the row, change status to RESOLVED, attach an admin note. Both buyer and creator are notified.'],
      ['Can admins see private buyer data?', 'Yes — but every read is logged in the audit trail.'],
      ['How do I suspend a portfolio?', 'Portfolios tab → row → click "Suspend". Status flips immediately.'],
      ['How do plans take effect?', 'Plans tab — toggle a plan and price reflects on creator subscription on next renewal.'],
      ['How do I rotate a creator out of an opportunity?', 'Use the user detail and reassign the broker_id manually if needed.'],
      ['Where do I see audit logs?', 'Settings → Audit. Filter by user and action.'],
    ],
  },
};

export default function HelpPage({ user }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(user?.role && ROLE_DATA[user.role] ? user.role : 'creator');
  const [open, setOpen] = useState(0);
  const data = ROLE_DATA[tab];

  return (
    <div data-testid="help-page" style={{ padding: '40px', maxWidth: '1000px', margin: '0 auto' }}>
      <GuidedTour user={user} />
      <h1 style={{ fontSize: '32px', fontWeight: 700, color: theme.colors.text.primary, fontFamily: theme.fonts.heading, marginBottom: '8px' }}>
        Help & Support
      </h1>
      <p style={{ fontSize: '15px', color: theme.colors.text.secondary, marginBottom: '20px' }}>
        Everything you need across creators, buyers, brokers and admins.
      </p>

      {/* Role tabs */}
      <div data-testid="help-role-tabs" style={{ display: 'flex', gap: '6px', marginBottom: '20px', borderBottom: `1px solid ${theme.colors.borders}` }}>
        {Object.keys(ROLE_DATA).map(k => (
          <button
            key={k}
            data-testid={`help-tab-${k}`}
            onClick={() => { setTab(k); setOpen(0); }}
            style={{
              padding: '10px 18px', border: 'none', cursor: 'pointer', background: 'transparent',
              fontSize: '14px', fontWeight: 700,
              color: tab === k ? theme.colors.primary : theme.colors.text.muted,
              borderBottom: tab === k ? `2px solid ${theme.colors.primary}` : '2px solid transparent',
              marginBottom: '-1px',
            }}>
            {ROLE_DATA[k].label}
          </button>
        ))}
      </div>

      {/* Start Guided Tour */}
      <button data-testid="help-start-tour"
        onClick={() => window.dispatchEvent(new Event('guided-tour:start'))}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '8px',
          padding: '10px 18px', background: theme.colors.primary, color: '#FFF',
          border: 'none', borderRadius: theme.radius.sm, cursor: 'pointer',
          fontWeight: 700, fontSize: '13px', marginBottom: '20px',
        }}>
        <Sparkles size={14} /> Start Guided Tour ({user?.role || 'current role'})
      </button>

      {/* Quick Links */}
      <div data-testid="help-quicklinks" style={{ background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card, padding: '20px 24px', marginBottom: '20px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '12px' }}>Quick Links — {data.label}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px' }}>
          {data.quickLinks.map(([label, to]) => (
            <button key={to}
              data-testid={`help-quicklink-${to.replaceAll('/', '-').replaceAll('?','-').replaceAll('=','-').replaceAll('&','-')}`}
              onClick={() => navigate(to)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px', background: theme.colors.bg.secondary,
                border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.sm,
                cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                color: theme.colors.primary, textAlign: 'left',
              }}>
              {label}
              <ChevronRight size={14} color={theme.colors.text.muted} />
            </button>
          ))}
        </div>
      </div>

      {/* First 5 minutes */}
      <div data-testid="help-first5" style={{ background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card, padding: '20px 24px', marginBottom: '20px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '8px' }}>First 5 minutes — {data.label}</h3>
        <ol style={{ margin: 0, paddingLeft: '20px', color: theme.colors.text.secondary, fontSize: '13px', lineHeight: 1.8 }}>
          {data.fiveMin.map((line, i) => <li key={i}>{line}</li>)}
        </ol>
      </div>

      {/* FAQ accordion (8 per role) */}
      <div data-testid="help-faqs" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
        {data.faqs.map(([q, a], i) => {
          const isOpen = open === i;
          return (
            <div key={i} data-testid={`help-faq-${tab}-${i}`} style={{ background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.md }}>
              <button onClick={() => setOpen(isOpen ? -1 : i)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '14px 18px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                <span style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>{q}</span>
                <ChevronDown size={14} color={theme.colors.text.muted} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>
              {isOpen && (
                <p style={{ padding: '0 18px 16px', margin: 0, fontSize: '13px', color: theme.colors.text.secondary, lineHeight: 1.6 }}>{a}</p>
              )}
            </div>
          );
        })}
      </div>

      <div data-testid="help-contact" style={{ background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card, padding: '20px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <Mail size={16} color={theme.colors.primary} />
          <h3 style={{ fontSize: '14px', fontWeight: 700, color: theme.colors.text.primary, margin: 0 }}>Contact Support</h3>
        </div>
        <p style={{ fontSize: '13px', color: theme.colors.text.secondary, margin: 0 }}>
          Email <a href="mailto:support@kickback.contracts" style={{ color: theme.colors.primary, fontWeight: 600 }}>support@kickback.contracts</a>. Reply within one business day.
        </p>
      </div>
    </div>
  );
}
